import type { CanvasSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, round } from '../util/safe.js';
import { hash } from '../util/hash.js';

/**
 * Canvas-отпечаток и — что важнее — детект рандомизации.
 *
 * Сам по себе хеш canvas сегодня почти бесполезен: его подделывают все.
 * Ценность в другом — антидетект-браузеры добавляют в пиксели случайный шум,
 * чтобы хеш не был стабильным между сайтами. Побочный эффект: он перестаёт
 * быть стабильным и внутри одной страницы. Отрисовываем одно и то же
 * несколько раз подряд: настоящий браузер даст идентичные байты всегда.
 */

const WIDTH = 300;
const HEIGHT = 150;
const REPEATS = 4;

function makeCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  return attempt(() => {
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext('2d');
    return ctx ? { canvas, ctx } : null;
  });
}

function renderGeometry(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  ctx.globalCompositeOperation = 'multiply';

  const circles: [string, number, number][] = [
    ['#f2f', 50, 50],
    ['#2ff', 100, 50],
    ['#ff2', 75, 100],
  ];
  for (const [color, x, y] of circles) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, 50, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
  }

  // Кольцо через evenodd: заливка по чётности — частый источник различий
  // между реализациями растеризатора.
  ctx.fillStyle = '#f9c';
  ctx.beginPath();
  ctx.arc(75, 75, 75, 0, Math.PI * 2, true);
  ctx.arc(75, 75, 25, 0, Math.PI * 2, true);
  ctx.fill('evenodd');

  // Градиент и тень задействуют другой путь в графическом бэкенде.
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
  gradient.addColorStop(0, 'rgba(255,0,0,0.35)');
  gradient.addColorStop(0.5, 'rgba(0,255,0,0.35)');
  gradient.addColorStop(1, 'rgba(0,0,255,0.35)');
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = gradient;
  ctx.fillRect(150, 10, 140, 60);

  ctx.shadowBlur = 8;
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.strokeStyle = '#123456';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(160, 90);
  ctx.bezierCurveTo(200, 80, 240, 140, 285, 100);
  ctx.stroke();
  ctx.shadowBlur = 0;
}

function renderText(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);

  // Смесь алфавитов и кернинг-пар: растеризация шрифта различается
  // между ОС даже при одинаковом семействе.
  ctx.fillStyle = '#069';
  ctx.font = '11pt "Times New Roman", serif';
  ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 2, 15);

  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.font = '18pt Arial, sans-serif';
  ctx.fillText('Съешь ещё этих мягких булок, 0123', 4, 45);

  ctx.font = '14px monospace';
  ctx.fillStyle = '#000';
  ctx.fillText('AVATAR WAVE fi fl ﬁ ﬂ ¼ ½', 2, 70);

  ctx.textBaseline = 'top';
  ctx.font = '13px "Not A Real Font", cursive';
  ctx.fillText('fallback metrics probe', 2, 100);
}

function renderEmoji(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  ctx.textBaseline = 'top';
  ctx.font = '28px sans-serif';
  // Эмодзи с ZWJ и модификаторами тона: набор глифов сильно зависит
  // от версии шрифта эмодзи, то есть от ОС и её версии.
  ctx.fillText('😃🧑‍🚀👨‍👩‍👧‍👦🏳️‍🌈👋🏽', 0, 0);
  ctx.fillText('🇷🇺🇺🇸🇩🇪 ⌚️🕰️', 0, 40);
  ctx.fillText('🫠🩷🪿🇺🇳', 0, 85);
}

function pixels(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): Uint8Array | null {
  return attempt(() => {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    return new Uint8Array(data.buffer.slice(0));
  });
}

/** Доля различающихся байтов между двумя буферами одинаковой длины. */
function diffRatio(a: Uint8Array, b: Uint8Array): number {
  if (a.length !== b.length || a.length === 0) return 1;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) diff++;
  }
  return diff / a.length;
}

export const canvasModule = defineModule({
  id: 'canvas',
  phase: 1,
  timeoutMs: 4000,
  run(): CanvasSignals {
    const made = makeCanvas();
    if (!made) {
      return {
        supported: false,
        geometryHash: null,
        textHash: null,
        emojiHash: null,
        combinedHash: null,
        repeatHashes: null,
        stable: null,
        noiseRatio: null,
        contextAttributes: null,
        windingSupported: null,
        dataUrlLength: null,
      };
    }

    const { canvas, ctx } = made;

    const hashOf = (render: (c: CanvasRenderingContext2D) => void): string | null => {
      return attempt(() => {
        render(ctx);
        const px = pixels(canvas, ctx);
        return px ? hash(px) : null;
      });
    };

    const geometryHash = hashOf(renderGeometry);
    const textHash = hashOf(renderText);
    const emojiHash = hashOf(renderEmoji);

    // Повторяем геометрию: одинаковые входные данные обязаны дать
    // одинаковые байты. Расхождение = внесённый шум.
    const repeatHashes: string[] = [];
    let firstPixels: Uint8Array | null = null;
    let maxNoise = 0;
    for (let i = 0; i < REPEATS; i++) {
      const result = attempt(() => {
        renderGeometry(ctx);
        return pixels(canvas, ctx);
      });
      if (!result) continue;
      repeatHashes.push(hash(result));
      if (firstPixels) {
        maxNoise = Math.max(maxNoise, diffRatio(firstPixels, result));
      } else {
        firstPixels = result;
      }
    }

    const stable = repeatHashes.length > 1 ? new Set(repeatHashes).size === 1 : null;

    return {
      supported: true,
      geometryHash,
      textHash,
      emojiHash,
      combinedHash:
        geometryHash && textHash && emojiHash
          ? hash(`${geometryHash}|${textHash}|${emojiHash}`)
          : null,
      repeatHashes,
      stable,
      noiseRatio: repeatHashes.length > 1 ? round(maxNoise, 6) : null,
      contextAttributes: attempt(
        () => ctx.getContextAttributes() as unknown as Record<string, unknown>,
      ),
      windingSupported: attempt(() => {
        ctx.rect(0, 0, 10, 10);
        ctx.rect(2, 2, 6, 6);
        return !ctx.isPointInPath(5, 5, 'evenodd');
      }),
      dataUrlLength: attempt(() => {
        renderGeometry(ctx);
        return canvas.toDataURL().length;
      }),
    };
  },
});
